import React, { useState, useEffect } from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik, Field, Form } from 'formik';

import { APP_LABELS, FIELD_LENGTH } from '@config/app-config';
import Help from '@modules/shared/components/reusable-components/help';
import SuccessToaster from '@modules/shared/components/reusable-components/success-toaster';
import ErrorPopup from '@modules/employee-data-collection/components/upload-file/error-popup';
import PartialSaveWarningPopup from '@modules/shared/components/reusable-components/partial-save-popup';
import LastUpdateName from '@modules/annual-reports/components/anuual-reports-overview/last-update-name';

import {
  resetReduxState,
  getOvernightCampParticipantsAndEnrollment,
  postOvernightCampDataEnrollmentAndOccupancy
} from '@redux/overnight-camp-data-participations-and-enrollments/action';
import { getCampReportsDashboardOverviewData } from '@redux/overnight-camp-data-overview/action';

import { AssociationAdminLoginDetailsSelector } from '@redux/login/selectors';
import { ApiParaSelector } from '@redux/overnight-camp-data-apiParameter/selectors';
import { getOvernightCampParticipantsAndEnrollmentListSelector } from '@redux/overnight-camp-data-participations-and-enrollments/selectors';

import helpicon from '@static/images/caretdoubleright.svg';
import attentionicon from '@static/images/attention-icon.svg';

const createRowObject = sessionNumber => ({
  SessionId: sessionNumber,
  TotalNumberOfDays: '',
  TotalNumberOfCamperBedsAvailable: '',
  TotalNumberOfCamperBedsFilled: '',
  TotalSessionCost: ''
});

const EnrollmentAndOccupancy = props => {
  const {
    status,
    setLoader,
    LoginUserData,
    ApiParameterData,
    handleResetRedux,
    getEnrollmentData,
    handleGetEnrollmentData,
    handlePostEnrollmentData,
    handleGetCampReportsDashboard
  } = props;

  const role = get(LoginUserData, 'user.role[0]');

  const [help, setHelp] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorPopup, setErrorPopup] = useState(false);
  const [successPopup, setSuccessPopup] = useState(false);

  const [resStatus, setResStatus] = useState();
  const [popup, setPopup] = useState({ show: false, type: '', target: 0 });

  const [rowCount, setRowCount] = useState('0');
  const [appliedRows, setAppliedRows] = useState(0);
  const [allFormRows, setAllFormRows] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasAppliedOnce, setHasAppliedOnce] = useState(false);

  const handleShowHelp = () => {
    setHelp(true);
  };

  const hideShowHelp = () => {
    setHelp(false);
  };

  const handleGetCall = success => {
    setLoader(true);
    handleGetEnrollmentData(
      ApiParameterData?.branchNumber,
      ApiParameterData?.reportBatchId,
      ApiParameterData?.year
    )
      .then(res => {
        if (res && res.status && String(res.status).startsWith('2')) {
          setLoader(false);
          if (success) {
            setSuccessPopup(true);
          }
        } else {
          setLoader(false);
          setErrorMessage(APP_LABELS.COMMON_ERROR_MESSAGE);
          setErrorPopup(true);
        }
      })
      .catch(err => {
        setLoader(false);
        setErrorMessage(APP_LABELS.COMMON_ERROR_MESSAGE);
        setErrorPopup(true);
      });
  };

  useEffect(() => {
    handleGetCall();
    setInstructions(
      JSON.parse(sessionStorage.getItem('allinstructions'))[
        'enrollment-and-occupancy'
      ]?.Instruction
    );

    return () => {
      handleResetRedux('getOvernightCampEnrollment');
    };
  }, []);

  useEffect(() => {
    if (getEnrollmentData && getEnrollmentData.length > 0) {
      setAllFormRows(getEnrollmentData);
      setAppliedRows(getEnrollmentData.length);
      setRowCount(getEnrollmentData.length.toString());
      setHasAppliedOnce(true);
    }
  }, [getEnrollmentData]);

  const handleApply = () => {
    const newCount = parseInt(rowCount, 10);
    if (!hasAppliedOnce) {
      setAppliedRows(newCount);
      setAllFormRows(prev => {
        const updated = [...prev];
        for (let i = prev.length; i < newCount; i++) {
          updated.push(createRowObject(i + 1));
        }
        return updated;
      });
      setHasAppliedOnce(true);
    } else if (newCount > appliedRows) {
      setPopup({ show: true, type: 'add', target: newCount });
    } else if (newCount < appliedRows) {
      setPopup({ show: true, type: 'remove', target: newCount });
    }
  };

  const handleConfirm = () => {
    const newCount = parseInt(rowCount, 10);

    if (newCount > appliedRows) {
      setAllFormRows(prev => {
        const updated = [...prev];
        for (let i = prev.length; i < newCount; i++) {
          updated.push(createRowObject(i + 1));
        }
        return updated;
      });
      setAppliedRows(newCount);
      setPopup({ show: false, type: '', target: 0 });
    } else if (newCount < appliedRows) {
      setAllFormRows(prev => prev.slice(0, newCount));
      setAppliedRows(newCount);
      setPopup({ show: false, type: '', target: 0 });
    }
  };

  const handleCancel = () => {
    setRowCount(appliedRows);
    setPopup({ show: false, type: '', target: 0 });
  };

  const handleFieldChange = (e, index, field) => {
    const value = e.target.value.replace(/\D+/g, '');
    setAllFormRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    // Clear field error if it exists
    setFieldErrors(prev => ({ ...prev, [`${index}-${field}`]: '' }));
  };

  const handleBedsFilledBlur = index => {
    const filled = Number(allFormRows[index].TotalNumberOfCamperBedsFilled);
    const available = Number(
      allFormRows[index].TotalNumberOfCamperBedsAvailable
    );

    if (!isNaN(filled) && !isNaN(available) && filled > available) {
      setFieldErrors(prev => ({
        ...prev,
        [`${index}-TotalNumberOfCamperBedsFilled`]:
          '* Filled beds cannot exceed available beds'
      }));
    } else {
      setFieldErrors(prev => ({
        ...prev,
        [`${index}-TotalNumberOfCamperBedsFilled`]: ''
      }));
    }
  };

  const parseValue = val => {
    // If user entered nothing, keep null
    if (val === '' || val === null || val === undefined) {
      return null;
    }

    const num = Number(val);

    // If it's actually a number and user typed "0", keep 0
    if (!isNaN(num)) {
      return num;
    }

    return null;
  };

  const handleSave = () => {
    setLoader(true);
    const tableRows = allFormRows.slice(0, appliedRows).map(row => ({
      SessionId: row.SessionId,
      TotalNumberOfDays: parseValue(row.TotalNumberOfDays),
      TotalNumberOfCamperBedsAvailable: parseValue(
        row.TotalNumberOfCamperBedsAvailable
      ),
      TotalNumberOfCamperBedsFilled: parseValue(
        row.TotalNumberOfCamperBedsFilled
      ),
      TotalSessionCost: parseValue(row.TotalSessionCost)
    }));

    setSuccessPopup(false);
    const payload = {
      Enrollment: {
        EnrollmentAndOccupancyId:
          getEnrollmentData?.EnrollmentAndOccupancyId || 0,
        RecordStatus: getEnrollmentData[0]?.RecordStatus || '',
        Year: ApiParameterData?.year,
        BranchNumber: ApiParameterData?.branchNumber,
        ReportBatchId: ApiParameterData?.reportBatchId,
        SessionCount: appliedRows,
        CampSeason: tableRows,

        CreatedByPersonId: LoginUserData?.user?.personBranchId,
        UpdatedByPersonId: LoginUserData?.user?.personBranchId
      }
    };

    handlePostEnrollmentData(payload).then(res => {
      if (res.status === 201 && res?.data?.isSuccess) {
        setResStatus(res?.data?.RecordStatus || res?.data?.recordStatus);
        handleGetCall(true);
        handleGetCampReportsDashboard(
          ApiParameterData?.branchNumber,
          ApiParameterData?.year
        );
      } else if (res.statusCode === 409) {
        setLoader(false);
        setErrorMessage(res.errorMessage);
        setErrorPopup(true);
      } else {
        setLoader(false);
        setErrorPopup(true);
      }
    });
  };

  return (
    <div>
      <button
        type="button"
        className="ymca-help-link text-decoration-none ymca-up-data-help-link annual-report-help"
        onClick={handleShowHelp}
      >
        <span>HELP</span>
        <img src={helpicon} className="img-fluid" alt="caret-icon" />
      </button>

      <div className="ymca-program mb-2">
        <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 pb-3">
          <div className="ymca-instructionBoxes">
            <h4 className="ymca-instructHeadline">{APP_LABELS.INSTRUCTIONS}</h4>
            <p className="ymca-instructTitle">
              <div
                dangerouslySetInnerHTML={{
                  __html: instructions
                }}
              />
            </p>
          </div>
        </div>

        {getEnrollmentData &&
          getEnrollmentData[0]?.RecordStatus === 'Partial' && (
            <PartialSaveWarningPopup />
          )}

        <span className="ymca-program p-0">
          <h6 className="ymca-programActivities">
            How many Overnight Camp Sessions did you offer during this Past
            Summers Season?
          </h6>
          <h6 className="mt-3">
            Number of row(s) to appear in a table below *
          </h6>
        </span>

        <div className=" row">
          <div className="col-6">
            <div className="d-flex align-items-center ymca-selectWrap">
              <select
                value={rowCount}
                onChange={e => setRowCount(e.target.value)}
                className="ymca-fieldSet"
                defaultValue=""
              >
                <option value="">Select Number</option>
                {[...Array(20).keys()].map(num => (
                  <option key={num} value={num + 1}>
                    {num + 1}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn ymca-savingBTN ymca-btnRightUntitled mt-0 ms-3"
                onClick={handleApply}
                disabled={
                  role === APP_LABELS.YUSA_DATA_VIEWER ||
                  status?.toUpperCase() === APP_LABELS.STATUS_SUBMITTED
                }
              >
                Apply
              </button>
            </div>
          </div>
          <div className="col-6" />
        </div>

        {appliedRows > 0 && allFormRows.length > 0 && (
          <Formik
            initialValues={{ rows: allFormRows }}
            enableReinitialize
            onSubmit={handleSave}
          >
            {({ dirty }) => {
              sessionStorage.setItem('dirty', dirty);
              return (
                <Form>
                  <p className="ymca-programActivities">
                    Please provide the requested information for each of your
                    Overnight Camp Sessions
                  </p>
                  <div className="ymca-tableResponsive">
                    <table className="table ymca-table table-striped ymca-autoLength">
                      <thead>
                        <tr className="ymca-cuttingData">
                          <th>Session</th>
                          <th>Total Number Of Days</th>
                          <th>Total Number Of Camper Beds Available</th>
                          <th>Total Number Of Camper Beds Filled</th>
                          <th>Program Fee (Per Person)</th>
                        </tr>
                      </thead>
                      <tbody className="ymca-cuttingBody ymca-lengths">
                        {allFormRows.slice(0, appliedRows).map((row, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>
                              <Field name={`rows[${idx}].TotalNumberOfDays`}>
                                {() => (
                                  <input
                                    className="form-control"
                                    type="text"
                                    value={row.TotalNumberOfDays}
                                    maxLength={FIELD_LENGTH.TEXTBOX_LENGTH}
                                    onChange={e =>
                                      handleFieldChange(
                                        e,
                                        idx,
                                        'TotalNumberOfDays'
                                      )
                                    }
                                  />
                                )}
                              </Field>
                            </td>
                            <td>
                              <Field
                                name={`rows[${idx}].TotalNumberOfCamperBedsAvailable`}
                              >
                                {() => (
                                  <input
                                    className="form-control"
                                    type="text"
                                    value={row.TotalNumberOfCamperBedsAvailable}
                                    maxLength={FIELD_LENGTH.TEXTBOX_LENGTH}
                                    onChange={e =>
                                      handleFieldChange(
                                        e,
                                        idx,
                                        'TotalNumberOfCamperBedsAvailable'
                                      )
                                    }
                                    onBlur={() => handleBedsFilledBlur(idx)}
                                  />
                                )}
                              </Field>
                            </td>
                            <td>
                              <Field
                                name={`rows[${idx}].TotalNumberOfCamperBedsFilled`}
                              >
                                {() => (
                                  <>
                                    <input
                                      className="form-control"
                                      type="text"
                                      value={row.TotalNumberOfCamperBedsFilled}
                                      maxLength={FIELD_LENGTH.TEXTBOX_LENGTH}
                                      onChange={e =>
                                        handleFieldChange(
                                          e,
                                          idx,
                                          'TotalNumberOfCamperBedsFilled'
                                        )
                                      }
                                      onBlur={() => handleBedsFilledBlur(idx)}
                                    />
                                    {fieldErrors[
                                      `${idx}-TotalNumberOfCamperBedsFilled`
                                    ] && (
                                      <div className="errorNote">
                                        {
                                          fieldErrors[
                                            `${idx}-TotalNumberOfCamperBedsFilled`
                                          ]
                                        }
                                      </div>
                                    )}
                                  </>
                                )}
                              </Field>
                            </td>
                            <td>
                              <Field name={`rows[${idx}].TotalSessionCost`}>
                                {() => (
                                  <input
                                    className="form-control"
                                    type="text"
                                    value={row.TotalSessionCost}
                                    maxLength={FIELD_LENGTH.TEXTBOX_LENGTH}
                                    onChange={e =>
                                      handleFieldChange(
                                        e,
                                        idx,
                                        'TotalSessionCost'
                                      )
                                    }
                                  />
                                )}
                              </Field>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {getEnrollmentData && (
                    <div className="ymca-submitHighlighter">
                      <LastUpdateName
                        Username={getEnrollmentData[0]?.UpdatedByName}
                        Time={getEnrollmentData[0]?.UpdatedDatetime}
                      />
                    </div>
                  )}
                  {((appliedRows > 0 && allFormRows.length > 0) ||
                    role !== APP_LABELS.YUSA_DATA_VIEWER) && (
                    <button
                      type="submit"
                      className="btn ymca-savingBTN ymca-btnRightUntitled float-end mt-5"
                      disabled={
                        status?.toUpperCase() === APP_LABELS.STATUS_SUBMITTED ||
                        Object.values(fieldErrors).some(err => err)
                      }
                    >
                      Save
                    </button>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
      </div>
      {successPopup && <SuccessToaster Status={resStatus} />}

      <ErrorPopup
        handleCloseErrorPopup={() => {
          setErrorPopup(false);
        }}
        errorMessage={errorMessage}
        showErrorPopup={errorPopup}
      />

      {help && (
        <Help hidePopup={hideShowHelp} keyValue="enrollment-and-occupancy" />
      )}

      {popup.show && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content p-4 rounded-lg">
              <div className="d-flex justify-content-between align-items-start">
                <div className="d-flex align-items-start">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center mr-3 me-3"
                    style={{ width: '50px', height: '50px' }}
                  >
                    <img
                      src={attentionicon}
                      className="img-fluid"
                      alt="attentionicon"
                    />
                  </div>
                  <div>
                    <h3 className="mb-1">Attention!</h3>
                    {popup.type === 'add' && (
                      <p className="ymca-programActivities mb-0">
                        Are you sure you want to add multiple rows as in the
                        table?
                      </p>
                    )}
                    {popup.type === 'remove' && (
                      <p className="ymca-programActivities mb-0">
                        {popup.target + 1 === appliedRows
                          ? `Are you sure you want to remove row no. ${appliedRows}  and their data?`
                          : `Are you sure you want to remove rows no. ${popup.target + 1} 
                        to ${appliedRows} and their data?`}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-light rounded-circle p-0"
                  style={{ width: '30px', height: '30px' }}
                  onClick={handleCancel}
                >
                  <i className="fas fa-times text-info" />
                </button>
              </div>

              <div className="mt-4 d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-primary mr-2 me-4"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn ymca-savingBTN ymca-btnRightUntitled mt-0 ms-3"
                  onClick={handleConfirm}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

EnrollmentAndOccupancy.propTypes = {
  setLoader: PropTypes.func.isRequired,
  handleResetRedux: PropTypes.func.isRequired,
  handleGetEnrollmentData: PropTypes.func.isRequired,
  handlePostEnrollmentData: PropTypes.func.isRequired,
  handleGetCampReportsDashboard: PropTypes.func.isRequired,

  status: PropTypes.objectOf(PropTypes.any).isRequired,
  LoginUserData: PropTypes.objectOf(PropTypes.any).isRequired,
  ApiParameterData: PropTypes.objectOf(PropTypes.any).isRequired,
  getEnrollmentData: PropTypes.objectOf(PropTypes.any).isRequired
};

const mapDispatchToProps = {
  handleResetRedux: resetReduxState,
  handleGetCampReportsDashboard: getCampReportsDashboardOverviewData,
  handleGetEnrollmentData: getOvernightCampParticipantsAndEnrollment,
  handlePostEnrollmentData: postOvernightCampDataEnrollmentAndOccupancy
};

const mapStateToProps = state => ({
  ApiParameterData: ApiParaSelector(state),
  LoginUserData: AssociationAdminLoginDetailsSelector(state),
  getEnrollmentData:
    getOvernightCampParticipantsAndEnrollmentListSelector(state)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(EnrollmentAndOccupancy));
